import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { purchaseOrderApi } from '@/services/api'
import type { PurchaseOrder } from '@/types'
import { Plus, Ban, Trash2, FileText, Send, CheckCircle, Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

const STATUS_TABS = ['all', 'draft', 'sent', 'partial', 'billed', 'voided'] as const
type StatusTab = typeof STATUS_TABS[number]

const STATUS_VARIANT: Record<string, 'default' | 'info' | 'warning' | 'success' | 'destructive'> = {
  draft: 'default',
  sent: 'info',
  partial: 'warning',
  billed: 'success',
  voided: 'default',
}

export default function PurchaseOrderList() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [pos, setPos] = useState<PurchaseOrder[]>([])
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [loading, setLoading] = useState(true)
  const [premiumGated, setPremiumGated] = useState(false)
  const [sending, setSending] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) return
    purchaseOrderApi.list(clientId)
      .then((res) => setPos(res.data.purchase_orders))
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 402) setPremiumGated(true)
        else toast.error('Failed to load purchase orders')
      })
      .finally(() => setLoading(false))
  }, [clientId])

  const filtered = activeTab === 'all' ? pos : pos.filter((p) => p.status === activeTab)

  function errMsg(err: unknown) {
    return (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed'
  }

  async function handleConvert(po: PurchaseOrder) {
    try {
      await purchaseOrderApi.convertToBill(po.id)
      // Reload to get updated status/billed_total
      const res = await purchaseOrderApi.list(clientId!)
      setPos(res.data.purchase_orders)
      toast.success(`Bill created from ${po.po_number}`)
    } catch (err: unknown) {
      toast.error(errMsg(err))
    }
  }

  async function handleSendEmail(po: PurchaseOrder) {
    if (!po.supplier_email) {
      toast.error('Add a supplier email address before sending')
      return
    }
    setSending(po.id)
    try {
      const res = await purchaseOrderApi.send(po.id)
      setPos((prev) => prev.map((p) => (p.id === po.id ? res.data.purchase_order : p)))
      toast.success(`${po.po_number} sent to ${po.supplier_email}`)
    } catch (err: unknown) {
      toast.error(errMsg(err))
    } finally {
      setSending(null)
    }
  }

  async function handleMarkSent(po: PurchaseOrder) {
    try {
      const res = await purchaseOrderApi.markSent(po.id)
      setPos((prev) => prev.map((p) => (p.id === po.id ? res.data.purchase_order : p)))
      toast.success('Marked as sent')
    } catch (err: unknown) {
      toast.error(errMsg(err))
    }
  }

  async function handleApprove(po: PurchaseOrder) {
    try {
      const res = await purchaseOrderApi.approve(po.id)
      setPos((prev) => prev.map((p) => (p.id === po.id ? res.data.purchase_order : p)))
      toast.success('Purchase order approved')
    } catch (err: unknown) {
      toast.error(errMsg(err))
    }
  }

  async function handleCopy(po: PurchaseOrder) {
    try {
      const res = await purchaseOrderApi.copy(po.id)
      setPos((prev) => [res.data.purchase_order, ...prev])
      toast.success(`Copied as ${res.data.purchase_order.po_number}`)
    } catch (err: unknown) {
      toast.error(errMsg(err))
    }
  }

  async function handleVoid(po: PurchaseOrder) {
    if (!confirm(`Void purchase order ${po.po_number}?`)) return
    try {
      const res = await purchaseOrderApi.void(po.id)
      setPos((prev) => prev.map((p) => (p.id === po.id ? res.data.purchase_order : p)))
      toast.success('Purchase order voided')
    } catch (err: unknown) {
      toast.error(errMsg(err))
    }
  }

  async function handleDelete(po: PurchaseOrder) {
    if (!confirm(`Delete draft PO ${po.po_number}?`)) return
    try {
      await purchaseOrderApi.delete(po.id)
      setPos((prev) => prev.filter((p) => p.id !== po.id))
      toast.success('Purchase order deleted')
    } catch (err: unknown) {
      toast.error(errMsg(err))
    }
  }

  if (premiumGated) {
    return (
      <Card className="max-w-sm mx-auto mt-16">
        <CardContent className="p-10 text-center">
          <h2 className="text-[17px] font-bold text-[#000A1E] leading-[24px] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Purchase Orders require a Premium client. Upgrade in Client Settings → Plan.
          </p>
          <Button onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade to Premium</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Purchase Orders</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Track orders to suppliers before billing</p>
        </div>
        <Button variant="create" onClick={() => navigate(`/clients/${clientId}/purchase-orders/new`)}>
          <Plus className="w-4 h-4" /> New PO
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-0 border-b border-border">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-3 py-2 text-xs font-medium capitalize transition-colors border-b-2 -mb-px',
              activeTab === tab
                ? 'text-[#0078C8] border-[#0078C8]'
                : 'text-[rgba(0,10,30,0.75)] border-transparent hover:text-[#000A1E]'
            )}
          >
            {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>PO #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Delivery Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Billed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-[13px] text-[rgba(0,10,30,0.75)]">
                      No purchase orders found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((po) => {
                    const canConvert = po.status === 'draft' || po.status === 'sent' || po.status === 'partial'
                    const canVoid = po.status !== 'billed' && po.status !== 'voided'
                    return (
                      <TableRow key={po.id}>
                        <TableCell>
                          <button
                            onClick={() => navigate(`/clients/${clientId}/purchase-orders/${po.id}/edit`)}
                            className="text-[13px] font-bold text-[#0078C8] hover:underline"
                          >
                            {po.po_number}
                          </button>
                          {po.approved_at && (
                            <p className="text-[10px] text-emerald-600 mt-0.5">Approved</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-[13px] text-[#000A1E]">{po.supplier_name}</p>
                          {po.supplier_email && (
                            <p className="text-xs text-[rgba(0,10,30,0.75)]">{po.supplier_email}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">{po.issue_date}</TableCell>
                        <TableCell className="text-[13px] text-[rgba(0,10,30,0.75)]">
                          {po.delivery_date || <span className="text-gray-300">—</span>}
                        </TableCell>
                        <TableCell className="text-right text-[13px] text-[#000A1E] tabular-nums">
                          {formatCurrency(po.total, po.currency)}
                        </TableCell>
                        <TableCell className="text-right text-[13px] tabular-nums">
                          {parseFloat(po.billed_total) > 0 ? (
                            <span className="text-emerald-600 font-medium">
                              {formatCurrency(po.billed_total, po.currency)}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[po.status] ?? 'default'}>
                            {po.status.charAt(0).toUpperCase() + po.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {po.status === 'draft' && !po.approved_at && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleApprove(po)} title="Approve">
                                <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                              </Button>
                            )}
                            {po.status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                disabled={sending === po.id}
                                onClick={() => po.supplier_email ? handleSendEmail(po) : handleMarkSent(po)}
                                title={po.supplier_email ? `Send to ${po.supplier_email}` : 'Mark as Sent'}
                              >
                                <Send className={`w-3.5 h-3.5 ${po.supplier_email ? 'text-blue-500' : 'text-gray-400'}`} />
                              </Button>
                            )}
                            {canConvert && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleConvert(po)} title="Convert to Bill">
                                <FileText className="w-3.5 h-3.5 text-emerald-600" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon-sm" onClick={() => handleCopy(po)} title="Copy PO">
                              <Copy className="w-3.5 h-3.5 text-gray-500" />
                            </Button>
                            {canVoid && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleVoid(po)} title="Void">
                                <Ban className="w-3.5 h-3.5 text-amber-500" />
                              </Button>
                            )}
                            {po.status === 'draft' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(po)} title="Delete"
                                className="text-red-400 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
